<script setup>
import { onMounted, onUnmounted, computed } from 'vue';
import { Bell } from 'lucide-vue-next'; // Icon for the trigger
import { useNotificationStore } from '@/stores/notificationStore';

// Shadcn Components
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import NotificationItem from './NotificationItem.vue';
import router from '@/router/index.js'
import { useAuthStore } from '@/stores/auth.js'
import { toast } from 'vue-sonner'

const store = useNotificationStore();

// Assuming your generic store puts the list in `store.data` or `store.items`
const notifications = computed(() => store.items || []);
const userId = useAuthStore().user?.id

onMounted(() => {
  store.fetchUnreadCount();
  const toastMethods = {
    success: toast.success,
    error: toast.error,
    warning: toast.warning,
    info: toast.info,
  };

  toast('Event received!')
  if (userId) {
    window.Echo.private(`App.Models.User.${userId}`).notification((notification) => {
      console.log('Reverb notification received:', notification);
      const variant = notification.data.variant;
      const triggerToast = toastMethods[variant] || toast;
      // 1. Update the Store (Add to list + Increment Badge)
      store.handleRealtimeNotification(notification);

      // 2. Show a Toast (Optional but recommended)
      triggerToast(notification.title || 'New Notification', {
        description: notification.message,
        variant: 'info',
        action: {
          label: 'View',
          onClick: () => store.markAsRead(notification.id)
        },
      });
    });
  }
});

onUnmounted(() => {
  // C. Cleanup Listener
  // Important: prevents duplicate listeners if the component is re-mounted
  if (userId) {
    window.Echo.leave(`App.Models.User.${userId}`);
  }
});

const handleOpenChange = (open) => {
  if (open && notifications.value.length === 0) {
    // Trigger the generic fetch method
    // Assuming your generic store has 'fetch' or 'load'
    store.fetchItems();
  }
};

const handleItemClick = async (notification) => {
  await store.markAsRead(notification.id);

  if (notification.data.action_url) {
    // window.location.href = notification.data.action_url;
    console.log('Navigating to:', notification);
    await router.push(notification.data.action_url)
  }
};
</script>

<template>
  <Popover @update:open="handleOpenChange">
    <PopoverTrigger as-child>
      <Button variant="ghost" size="icon" class="relative">
        <Bell class="h-7 w-7" />
        <Badge v-if="store.unreadCount > 0" class="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs text-white" variant="destructive">
          {{ store.unreadCount > 99 ? '99+' : store.unreadCount }}
        </Badge>
      </Button>
    </PopoverTrigger>

    <PopoverContent class="w-80 p-0" align="end">
      <div class="flex items-center justify-between p-4 pb-2">
        <h4 class="font-semibold leading-none">
          Notifications
        </h4>
        <Button variant="ghost" class="h-auto p-0 text-xs text-muted-foreground hover:text-foreground" @click="store.markAllAsRead">
          Mark all as read
        </Button>
      </div>
      <Separator />

      <ScrollArea class="h-[400px]">
        <div v-if="notifications.length === 0" class="flex flex-col items-center justify-center h-32 text-muted-foreground">
          <p class="text-sm">
            No notifications
          </p>
        </div>

        <div v-else class="flex flex-col">
          <NotificationItem v-for="notification in notifications" :key="notification.id" :notification="notification" @click="handleItemClick" />
          <Button v-if="store.next_page_url || store.meta?.current_page < store.meta?.last_page" variant="ghost" class="w-full rounded-none text-xs py-4 h-auto" :disabled="store.loading" @click="store.fetch()">
            {{ store.loading ? 'Loading...' : 'Load more' }}
          </Button>
        </div>
      </ScrollArea>
    </PopoverContent>
  </Popover>
</template>
